import { useCallback, useRef, useState } from "react";
import { governanceErrorMessage, governanceErrorStatus } from "../../../../utils/icGovernance";

/**
 * Busy / error state for one governance action form. `run(fn)` ignores a
 * second call while one is in flight, and answers the result or `undefined`
 * on failure (the message is in `error`; `conflict` is true on a 409 so the
 * form can offer "Reload").
 */
export const useGovAction = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const busyRef = useRef(false);

  const run = useCallback(async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
    if (busyRef.current) return undefined;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setConflict(false);
    try {
      return await fn();
    } catch (err) {
      console.error("Governance action failed:", err);
      setError(governanceErrorMessage(err));
      setConflict(governanceErrorStatus(err) === 409);
      return undefined;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, []);

  const clear = useCallback(() => {
    setError(null);
    setConflict(false);
  }, []);

  return { busy, error, conflict, run, clear };
};

export type GovAction = ReturnType<typeof useGovAction>;
